// HitosManager — gestión inline de próximos hitos por cliente.
//
// Persistencia inmediata vía validation-store. Soporta:
//   - "+ Agregar hito" con form inline (descripción, fecha objetivo,
//     responsable).
//   - Toggle de completado vía checkbox.
//   - Eliminación con confirmación.
//
// Schema persistido (validacion.json → clientes[<id>].hitos[]):
//   { id, descripcion, fechaObjetivo, responsable,
//     creado, creadoPor, completado, completadoEn }

import { html } from 'htm/preact'
import { useEffect, useMemo, useState } from 'preact/hooks'

import {
  addHito,
  toggleHitoCompletado,
  removeHito,
  getClienteState,
  subscribeValidation
} from '../lib/validation-store.js'
import { showToast } from '../lib/toast-bus.js'
import { getActiveAccount } from '../auth/auth-provider.js'

function sortHitos(hitos) {
  return [...hitos].sort((a, b) => {
    if (!!a.completado !== !!b.completado) return a.completado ? 1 : -1
    if (a.fechaObjetivo && b.fechaObjetivo) {
      return a.fechaObjetivo.localeCompare(b.fechaObjetivo)
    }
    if (a.fechaObjetivo) return -1
    if (b.fechaObjetivo) return 1
    return (a.creado || '').localeCompare(b.creado || '')
  })
}

function formatFecha(iso) {
  if (!iso) return null
  // ISO YYYY-MM-DD se interpreta como UTC; agregamos 'T00:00' para que
  // Date lo trate como local y no muestre el día anterior por zona.
  const d = iso.length === 10 ? new Date(`${iso}T00:00:00`) : new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  })
}

export function HitosManager({ clienteId }) {
  const account = useMemo(() => getActiveAccount(), [])
  const creadoPor = account?.name || account?.username || 'Desconocido'
  const creadoPorEmail = account?.username || ''

  const [hitos, setHitos] = useState(() => getClienteState(clienteId).hitos || [])
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    descripcion: '',
    fechaObjetivo: '',
    responsable: ''
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setHitos(getClienteState(clienteId).hitos || [])
    const unsub = subscribeValidation(() => {
      setHitos(getClienteState(clienteId).hitos || [])
    })
    return unsub
  }, [clienteId])

  const sorted = useMemo(() => sortHitos(hitos), [hitos])
  const pendientes = sorted.filter((h) => !h.completado).length

  const resetForm = () => {
    setForm({ descripcion: '', fechaObjetivo: '', responsable: '' })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!form.descripcion.trim()) {
      showToast({
        message: 'La descripción del hito es obligatoria.',
        kind: 'warning'
      })
      return
    }
    setSaving(true)
    try {
      await addHito(clienteId, form, { creadoPor, creadoPorEmail })
      resetForm()
      setShowForm(false)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[HitosManager] addHito falló:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = (hito) => {
    toggleHitoCompletado(clienteId, hito.id).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[HitosManager] toggle falló:', err)
    })
  }

  const handleRemove = (hito) => {
    const confirmar = window.confirm(
      `¿Eliminar el hito "${hito.descripcion}"? Esta acción no se puede deshacer.`
    )
    if (!confirmar) return
    removeHito(clienteId, hito.id).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[HitosManager] remove falló:', err)
    })
  }

  return html`
    <div class="hitos">
      <header class="hitos__header">
        <div>
          <h2 class="hitos__title">Próximos hitos</h2>
          <p class="section__subtitle">
            ${hitos.length === 0
              ? 'Sin hitos registrados. Agregá el primero para coordinar próximos compromisos con Cowork.'
              : `${pendientes} pendiente${pendientes === 1 ? '' : 's'} de ${hitos.length} en total.`}
          </p>
        </div>
        <button
          type="button"
          class="btn btn--secondary"
          onClick=${() => {
            if (showForm) resetForm()
            setShowForm((v) => !v)
          }}
        >${showForm ? 'Cancelar' : '+ Agregar hito'}</button>
      </header>

      ${showForm
        ? html`
            <form class="hitos__form" onSubmit=${handleSubmit}>
              <label class="hitos__field hitos__field--full">
                <span class="hitos__field-label">Descripción <em>*</em></span>
                <input
                  type="text"
                  required
                  maxLength="200"
                  placeholder="Informe semestral AFAC enviado a AVSEC"
                  value=${form.descripcion}
                  onInput=${(e) =>
                    setForm((f) => ({ ...f, descripcion: e.target.value }))}
                />
              </label>
              <label class="hitos__field">
                <span class="hitos__field-label">Fecha objetivo</span>
                <input
                  type="date"
                  value=${form.fechaObjetivo}
                  onInput=${(e) =>
                    setForm((f) => ({ ...f, fechaObjetivo: e.target.value }))}
                />
              </label>
              <label class="hitos__field">
                <span class="hitos__field-label">Responsable</span>
                <input
                  type="text"
                  maxLength="80"
                  placeholder="Franco / Jorge / Cowork / UVA"
                  value=${form.responsable}
                  onInput=${(e) =>
                    setForm((f) => ({ ...f, responsable: e.target.value }))}
                />
              </label>
              <div class="hitos__form-actions">
                <button
                  type="submit"
                  class="btn btn--primary"
                  disabled=${saving}
                >${saving ? 'Guardando…' : 'Guardar hito'}</button>
              </div>
            </form>
          `
        : null}

      ${sorted.length > 0
        ? html`
            <ul class="hitos__list">
              ${sorted.map(
                (h) => html`
                  <li
                    class=${`hitos__item ${h.completado ? 'hitos__item--done' : ''}`}
                    key=${h.id}
                  >
                    <input
                      type="checkbox"
                      class="hitos__check"
                      checked=${!!h.completado}
                      onChange=${() => handleToggle(h)}
                      aria-label=${`Marcar "${h.descripcion}" como ${h.completado ? 'pendiente' : 'completado'}`}
                    />
                    <div class="hitos__body">
                      <div class="hitos__descripcion">${h.descripcion}</div>
                      <div class="hitos__meta">
                        ${h.fechaObjetivo
                          ? html`<span class="hitos__meta-item">Objetivo: ${formatFecha(h.fechaObjetivo)}</span>`
                          : null}
                        ${h.responsable
                          ? html`<span class="hitos__meta-item">Resp.: ${h.responsable}</span>`
                          : null}
                        <span class="hitos__meta-item">Creado por ${h.creadoPor || '—'}</span>
                        ${h.completado && h.completadoEn
                          ? html`<span class="hitos__meta-item">Completado el ${formatFecha(h.completadoEn)}</span>`
                          : null}
                      </div>
                    </div>
                    <button
                      type="button"
                      class="hitos__remove"
                      onClick=${() => handleRemove(h)}
                      aria-label="Eliminar hito"
                      title="Eliminar hito"
                    >×</button>
                  </li>
                `
              )}
            </ul>
          `
        : null}
    </div>
  `
}
